/**
 * Tarjeta "Tu jornada" del Inicio (006 · T025; research R-5; contracts/staff-ui.md §3;
 * design-system §8.1: "hora de entrada 36 px tabular, estado con punto Éxito y texto").
 *
 * Puramente presentacional: recibe el `JourneyCard` ya calculado por `journeyCardOf` y solo pinta
 * textos e íconos (icono **y** texto siempre, nunca solo color — §10). `data-kind` expone el
 * `kind` para las pruebas y para que el E2E pueda distinguir los cinco casos de `data-model.md`
 * §4.2 sin depender de textos.
 *
 * 008 · T096 (contracts/ui.md §2.1): ranura opcional `schedule` para la línea "Tu turno hoy"
 * (`features/schedule/today-shift.tsx`), bajo el estado y dentro de la misma card — sin card nueva.
 */
import { AlertTriangle, CheckCircle2, CircleDot, Clock3, type LucideIcon } from 'lucide-react';

import { Card, CardTitle } from '../../components/ui/card';
import { formatTime, t } from '../../i18n';

import { markLabel, type JourneyCard as JourneyCardData, type JourneyCardKind } from './journey';

import type { ReactNode } from 'react';

const STATUS_ICON: Record<JourneyCardKind, LucideIcon> = {
  WORKING: CircleDot,
  OPEN_SHIFT_EXCEEDED: AlertTriangle,
  DONE: CheckCircle2,
  NOT_STARTED: Clock3,
  UNRESOLVED: AlertTriangle,
};

const STATUS_TEXT_KEY: Record<JourneyCardKind, string> = {
  WORKING: 'home.journey.status.working',
  OPEN_SHIFT_EXCEEDED: 'home.journey.status.openShiftExceeded',
  DONE: 'home.journey.status.done',
  NOT_STARTED: 'home.journey.status.notStarted',
  UNRESOLVED: 'common.errors.ATTENDANCE_STATE_UNRESOLVED',
};

export interface JourneyCardProps {
  card: JourneyCardData;
  /** 008 · T096: línea "Tu turno hoy" (se pinta sola o no pinta nada). */
  schedule?: ReactNode;
}

/**
 * Línea de horas de la tarjeta: la entrada abierta (con la sede si es de hoy, o "· ayer"/la fecha
 * si no lo es — US1-1 vs. US1-8) o, sin una entrada abierta, las marcas vigentes de hoy en orden
 * ("Entrada 08:42 · Salida 18:06", US1-4). `null` sin nada que mostrar (US1-2).
 */
function hoursLineOf(card: JourneyCardData): string | null {
  if (card.entry !== null) {
    const time = formatTime(card.entry.serverTimestamp, card.entry.siteTimezone);
    const entryText = t('home.marks.checkIn', { time });
    return card.entry.dayLabel !== null
      ? `${entryText} · ${card.entry.dayLabel}`
      : `${entryText} · ${card.entry.siteName}`;
  }
  if (card.todayMarks.length > 0) {
    return card.todayMarks.map(markLabel).join(' · ');
  }
  return null;
}

export function JourneyCard({ card, schedule }: JourneyCardProps) {
  const Icon = STATUS_ICON[card.kind];
  const hoursLine = hoursLineOf(card);

  return (
    <Card data-testid="home-journey" data-kind={card.kind}>
      <CardTitle>{t('home.journey.title')}</CardTitle>
      <div className="mt-fm-3 flex flex-col gap-fm-2">
        {hoursLine !== null ? (
          <p
            data-testid="home-journey-hours"
            className="fm-tabular font-display text-fm-3xl font-extrabold text-fm-text"
          >
            {hoursLine}
          </p>
        ) : null}
        <p
          data-testid="home-journey-status"
          className="flex items-center gap-fm-2 text-fm-base text-fm-text"
        >
          <Icon aria-hidden="true" size={20} className="shrink-0 text-fm-primary-compact" />
          <span>{t(STATUS_TEXT_KEY[card.kind])}</span>
        </p>
        {schedule}
      </div>
    </Card>
  );
}
